// IconButton — the icon-only action primitive, pure.
//
// Sibling to Button (.claude/research/design/06): same near-monochrome register, same
// borderless→elevation discipline. An icon-only control has no visible text,
// so the label is NOT optional decoration — it is the control's ONLY name.
// `label` is therefore a REQUIRED typed prop, set verbatim as aria-label; the
// button is square (min-width == min-height) so the glyph reads as a target.
//
// Three purposeful variants — primary (the one accent use: the primary action
// in a view), secondary (neutral filled, a resting affordance), ghost
// (transparent, low-emphasis, fills on interaction). Variant names match
// Button's so the two action siblings share one vocabulary. Two sizes map to
// the control-size scale.
//
// Register choices this encodes: the accent is spent ONLY by `primary`;
// `secondary` and `ghost` stay neutral. No resting border — an edge is revealed
// by hover
// tint + the inset focus ring, never painted at rest.
//
// Pure/props-driven: `variant` + `size` pick the look; `label` names the
// control; `children` is the icon glyph; every other button attribute (onClick,
// disabled, aria-*, type…) passes straight through. `label` is a plain string
// (the CALLER resolves it through Lingui) so this primitive carries no i18n
// dependency of its own. `type` defaults to "button" so an IconButton inside a
// <form> never submits by accident.

import type { ButtonHTMLAttributes } from "react";
import { forwardRef } from "react";
import "./IconButton.css";

type IconButtonVariant = "primary" | "secondary" | "ghost";
type IconButtonSize = "sm" | "md";

// `label` overrides the optional aria-label from button attrs, making the
// accessible name mandatory for an otherwise text-free control.
export type IconButtonProps = Omit<
    ButtonHTMLAttributes<HTMLButtonElement>,
    "aria-label"
> & {
    readonly label: string;
    readonly variant?: IconButtonVariant;
    readonly size?: IconButtonSize;
};

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
    (
        {
            label,
            variant = "ghost",
            size = "md",
            type = "button",
            className,
            children,
            ...rest
        },
        ref,
    ) => (
        <button
            ref={ref}
            type={type}
            aria-label={label}
            className={`bilda-icon-btn bilda-icon-btn--${variant} bilda-icon-btn--${size}${
                className ? ` ${className}` : ""
            }`}
            {...rest}
        >
            {children}
        </button>
    ),
);
